// src/pages/ProyectosPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header'; // IMPORTAMOS EL HEADER
import LogoAlgorithmia from '../../src/assets/LOGOAZUL-Ajpg.jpg';
import SEO from '../components/SEO';
import { 
  ArrowLeftIcon, 
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
  MapPinIcon,
  DocumentMagnifyingGlassIcon,
  UserGroupIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BellAlertIcon,
  ScaleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CodeBracketIcon,
  EyeIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ArrowTopRightOnSquareIcon,
  CursorArrowRaysIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  CloudIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

// ================================================
// SISTEMA DE COLORES Y PALETAS UNIFICADAS
// ================================================

// Paleta de colores principal (coincide con AboutPage)
const colorPalette = {
  primary: {
    dark: '#0E2544',     // Azul marino oscuro
    medium: '#2D4A7D',   // Azul medio
    light: '#1c3558',    // Azul intermedio
  },
  secondary: {
    green: '#4ADE80',    // Verde brillante
    teal: '#3BC571',     // Verde esmeralda
    lightGreen: '#2AAE60' // Verde oscuro
  },
  accents: {
    white: '#FFFFFF',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    }
  },
  gradients: {
    hero: 'from-[#0E2544] via-[#1c3558] to-[#2D4A7D]',
    primary: 'from-[#4ADE80] to-[#3BC571]',
    secondary: 'from-[#0E2544] to-[#2D4A7D]',
    accent: 'from-[#2D4A7D] to-[#4ADE80]'
  }
};

// ================================================
// COMPONENTE PRINCIPAL
// ================================================

const ProyectosPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 6;
  const [showContactModal, setShowContactModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  // Efecto para manejar scroll del body cuando el modal está abierto
  useEffect(() => {
    if (showContactModal || showMeetingModal || selectedProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showContactModal, showMeetingModal, selectedProject]);

  // ================================================
  // DATOS DE PROYECTOS MEJORADOS CON PALETA UNIFICADA
  // ================================================

  const proyectos = [
    {
      id: 1,
      title: "Sistema de Inspecciones",
      category: "PWA Avanzado",
      description: "Aplicativo web PWA para inspecciones de mantenimiento, seguridad industrial y medio ambiente con checklist personalizables y modo offline.",
      longDescription: "Sistema completo para realizar inspecciones técnicas con capacidades offline, captura de evidencias fotográficas y generación automática de reportes. Desarrollado con React y tecnología PWA para máxima accesibilidad. Implementa notificaciones push, sincronización en segundo plano y almacenamiento local para funcionamiento en áreas sin conexión.",
      features: ["Checklists personalizables", "Modo offline completo", "Captura de evidencias multimedia", "Reportes automáticos PDF", "Geolocalización precisa", "Firma digital certificada", "Dashboard analítico en tiempo real"],
      technologies: ["React", "TypeScript", "PWA", "MongoDB", "Express.js", "AWS S3", "Service Workers"],
      status: "Completado" as const,
      icon: ClipboardDocumentCheckIcon,
      color: "green" as const,
      client: "Empresa Industrial S.A.",
      timeline: "3 meses",
      impact: "Reducción del 70% en tiempo de inspecciones"
    },
    {
      id: 2,
      title: "Gestión de Riesgos",
      category: "Sistema de Gestión",
      description: "Identificación de peligros y evaluación de riesgos en puestos de trabajo con matrices de riesgo interactivas.",
      longDescription: "Plataforma para gestión integral de riesgos laborales que incluye análisis por área, plan de acción y seguimiento continuo. Interfaz intuitiva con visualizaciones en tiempo real y análisis predictivo basado en datos históricos.",
      features: ["Matrices de riesgo interactivas", "Análisis por área/departamento", "Plan de acción automatizado", "Seguimiento continuo", "Dashboard interactivo", "Alertas automáticas", "Reportes ejecutivos"],
      technologies: ["React", "Node.js", "Chart.js", "PostgreSQL", "Docker", "Redis", "WebSockets"],
      status: "Completado" as const,
      icon: ExclamationTriangleIcon,
      color: "blue" as const,
      client: "Consultora de Seguridad Integral",
      timeline: "4 meses",
      impact: "Detección temprana del 85% de riesgos críticos"
    },
    {
      id: 3,
      title: "Órdenes de Trabajo",
      category: "Productividad",
      description: "Gestión de tareas con tablero Kanban y notificaciones automáticas por Telegram.",
      longDescription: "Sistema de gestión de órdenes de trabajo con seguimiento 7-3-1 días, asignación dinámica y notificaciones en tiempo real. Optimiza el flujo de trabajo mediante automatización de tareas repetitivas y priorización inteligente.",
      features: ["Kanban visual drag & drop", "Notificaciones Telegram/WhatsApp", "Seguimiento 7-3-1 días", "Asignación dinámica", "Priorización automática", "Reportes de productividad", "Integración calendario"],
      technologies: ["React", "Socket.io", "Telegram API", "MongoDB", "Node.js", "WebSockets", "Redis Cache"],
      status: "Completado" as const,
      icon: Squares2X2Icon,
      color: "cyan" as const,
      client: "Empresa de Mantenimiento Industrial",
      timeline: "2.5 meses",
      impact: "Optimización del 40% en flujo de trabajo"
    },
    {
      id: 4,
      title: "Registro de Visitas",
      category: "Control de Acceso",
      description: "Control de visitas con geolocalización, fotos y dictado por voz para registro eficiente.",
      longDescription: "Sistema completo para el control de visitas que incluye geolocalización precisa, captura multimedia y funcionalidad de voz a texto. Implementa reconocimiento facial opcional y generación de QR codes dinámicos.",
      features: ["Geolocalización avanzada", "Captura multimedia integrada", "Voz a texto automático", "Reportes PDF personalizados", "QR Code dinámico", "Dashboard en tiempo real", "Reconocimiento facial"],
      technologies: ["React Native", "Google Maps API", "Speech-to-Text", "PDFKit", "Firebase", "AWS S3", "Face Recognition API"],
      status: "En Desarrollo" as const,
      icon: MapPinIcon,
      color: "purple" as const,
      client: "Edificio Corporativo Smart Tower",
      timeline: "3 meses",
      impact: "Reducción del 60% en tiempo de registro"
    },
    {
      id: 5,
      title: "Investigación de Accidentes",
      category: "Seguridad Industrial",
      description: "Análisis con método de espina de pescado y 5 porqués para investigación profunda.",
      longDescription: "Herramienta especializada para investigación de incidentes utilizando metodologías Ishikawa y análisis de causa raíz. Integra machine learning para identificar patrones y prevenir incidentes futuros.",
      features: ["Diagramas Ishikawa interactivos", "Plan de acciones automatizado", "Asignación de responsables", "Seguimiento de medidas", "Plantillas predefinidas", "Reportes estadísticos", "Análisis predictivo"],
      technologies: ["React", "D3.js", "TypeScript", "MongoDB", "Express.js", "Chart.js", "Python ML"],
      status: "Completado" as const,
      icon: DocumentMagnifyingGlassIcon,
      color: "orange" as const,
      client: "Compañía Minera Nacional",
      timeline: "3 meses",
      impact: "Aumento del 70% en efectividad de investigaciones"
    },
    {
      id: 6,
      title: "Gestión de RRHH",
      category: "Recursos Humanos",
      description: "Administración de empleados y capacitaciones con búsqueda por DNI y gestión documental.",
      longDescription: "Plataforma integral para la gestión de recursos humanos que incluye registro completo de empleados, capacitaciones y documentación. Centraliza toda la información del personal con encriptación de datos sensible.",
      features: ["Registro completo de empleados", "Gestión de capacitaciones", "Búsqueda ágil por múltiples criterios", "Historial digital completo", "Documentos adjuntos encriptados", "Dashboard administrativo", "Reportes de rotación"],
      technologies: ["React", "Node.js", "PDF Generation", "OCR", "PostgreSQL", "AWS", "SSL/TLS"],
      status: "Completado" as const,
      icon: UserGroupIcon,
      color: "indigo" as const,
      client: "Empresa de Servicios Multicom",
      timeline: "4 meses",
      impact: "Centralización del 100% de datos RRHH"
    },
    {
      id: 7,
      title: "Firma Digital",
      category: "Documentación",
      description: "Sistema de firma digital con trazabilidad completa y certificación de fecha/hora.",
      longDescription: "Solución de firma digital empresarial con geolocalización, hora certificada y archivo seguro de documentos. Cumple con normativas internacionales de seguridad y ofrece auditoría completa.",
      features: ["Geolocalización certificada", "Fecha/hora blockchain", "Responsable identificado", "Archivo seguro encriptado", "Trazabilidad completa", "Encriptación AES-256", "Auditoría detallada"],
      technologies: ["React", "Digital Signature API", "AWS S3", "Blockchain", "Node.js", "SSL/TLS", "PDF.js"],
      status: "Completado" as const,
      icon: DocumentTextIcon,
      color: "cyan" as const,
      client: "Estudio Jurídico & Asociados",
      timeline: "2 meses",
      impact: "Eliminación del 90% de papel en procesos"
    },
    {
      id: 8,
      title: "Estudios Ergonómicos",
      category: "Salud Ocupacional",
      description: "Protocolos según normativa argentina con anexos completos y cálculos automáticos.",
      longDescription: "Sistema especializado para la realización de estudios ergonómicos conforme a la normativa argentina vigente. Automatiza cálculos complejos y genera reportes normativos con recomendaciones personalizadas.",
      features: ["Protocolos oficiales actualizados", "Cálculos automáticos complejos", "Recomendaciones personalizadas", "Reportes normativos listos", "Plantillas adaptables", "Análisis comparativo histórico", "Dashboard de seguimiento"],
      technologies: ["React", "TypeScript", "Chart.js", "MongoDB", "Python", "PDFKit", "Math.js"],
      status: "Planificado" as const,
      icon: AcademicCapIcon,
      color: "amber" as const,
      client: "Hospital Público Regional",
      timeline: "3 meses",
      impact: "Reducción del 50% en tiempo de evaluación"
    },
    {
      id: 9,
      title: "Mediciones Ambientales",
      category: "Medio Ambiente",
      description: "Registro de ruido e iluminación según resolución legal con alertas automáticas.",
      longDescription: "Sistema para medición y registro de variables ambientales conforme a la legislación vigente en materia de higiene y seguridad. Integración con sensores IoT y análisis en tiempo real.",
      features: ["Cumplimiento legal automatizado", "Registros históricos detallados", "Alertas de límites en tiempo real", "Reportes oficiales preformateados", "Dashboard analítico", "Integración IoT", "Calibración automática"],
      technologies: ["React", "IoT Integration", "Real-time Charts", "PostgreSQL", "MQTT", "WebSockets", "Python"],
      status: "Completado" as const,
      icon: ChartBarIcon,
      color: "pink" as const,
      client: "Fábrica Textil Moderna",
      timeline: "3.5 meses",
      impact: "Monitoreo del 100% de variables críticas"
    },
    {
      id: 10,
      title: "Avisos de Riesgos",
      category: "Gestión de Riesgos",
      description: "Gestión de alertas por criticidad y responsables con seguimiento automatizado.",
      longDescription: "Sistema para la gestión de avisos de riesgos con priorización automática y asignación por áreas responsables. Incluye workflow completo desde detección hasta cierre validado.",
      features: ["Priorización automática inteligente", "Asignación por áreas responsables", "Seguimiento automatizado", "Cierre validado por auditoría", "Notificaciones push multiplataforma", "Dashboard de control central", "Reportes de eficacia"],
      technologies: ["React", "Push Notifications", "WebSockets", "MongoDB", "Node.js", "Redis", "Workflow Engine"],
      status: "Completado" as const,
      icon: BellAlertIcon,
      color: "emerald" as const,
      client: "Petrolera Internacional Global",
      timeline: "2 meses",
      impact: "Reducción del 80% en tiempo de respuesta"
    },
    {
      id: 11,
      title: "Control de Pesaje",
      category: "Logística Inteligente",
      description: "Registro de kilos en cargas/descargas con PDF detallado y cálculo automático.",
      longDescription: "Sistema para el control de pesaje en operaciones logísticas con integración a balanzas digitales y generación automática de documentos. Incluye alertas de desviación y análisis de tendencias.",
      features: ["Cálculo automático preciso", "PDF personalizado profesional", "Histórico completo accesible", "Integración silos/balanzas", "Dashboard operativo en tiempo real", "Alertas de desviación automáticas", "Análisis de tendencias"],
      technologies: ["React", "Node.js", "PDF Generation", "Serial Port", "Express.js", "MySQL", "Chart.js"],
      status: "En Desarrollo" as const,
      icon: ScaleIcon,
      color: "rose" as const,
      client: "Empresa Logística Nacional",
      timeline: "4 meses",
      impact: "Optimización del 65% en proceso de pesaje"
    }
  ];

  // ================================================
  // FUNCIONES DE UTILIDAD CON PALETA UNIFICADA
  // ================================================

  // Filtrar proyectos
  const filteredProjects = proyectos.filter(proyecto => {
    const matchesSearch = proyecto.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proyecto.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proyecto.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proyecto.features.some(feature => 
                           feature.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filter === 'todos' || 
                         (filter === 'completados' && proyecto.status === 'Completado') ||
                         (filter === 'desarrollo' && proyecto.status === 'En Desarrollo') ||
                         (filter === 'planificados' && proyecto.status === 'Planificado');
    
    return matchesSearch && matchesFilter;
  });

  // Paginación
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  // Manejar paginación
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Funciones para manejar el modal de detalles
  const openProjectDetails = (id: number) => {
    setSelectedProject(id);
  };

  const closeProjectDetails = () => {
    setSelectedProject(null);
  };

  // Proyecto seleccionado
  const selectedProyecto = selectedProject ? proyectos.find(p => p.id === selectedProject) : null;

  // ================================================
  // SISTEMA DE COLORES DINÁMICO UNIFICADO
  // ================================================

  // Gradientes para proyectos usando paleta unificada
  const getGradientClass = (color: string) => {
    const gradients: Record<string, string> = {
      green: `from-[${colorPalette.secondary.green}] to-[${colorPalette.secondary.teal}]`,
      blue: `from-[${colorPalette.primary.dark}] to-[${colorPalette.primary.medium}]`,
      teal: `from-[${colorPalette.secondary.teal}] to-[${colorPalette.secondary.lightGreen}]`,
      purple: 'from-[#9333EA] to-[#7C3AED]',
      orange: 'from-[#F97316] to-[#EA580C]',
      indigo: 'from-[#4F46E5] to-[#4338CA]',
      cyan: 'from-[#06B6D4] to-[#0891B2]',
      pink: 'from-[#EC4899] to-[#DB2777]',
      emerald: 'from-[#10B981] to-[#059669]',
      amber: 'from-[#F59E0B] to-[#D97706]',
      rose: 'from-[#F43F5E] to-[#E11D48]'
    };
    return gradients[color] || `from-[${colorPalette.secondary.green}] to-[${colorPalette.secondary.teal}]`;
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Completado': return `bg-green-100 text-green-800`;
      case 'En Desarrollo': return `bg-blue-100 text-blue-800`;
      case 'Planificado': return `bg-amber-100 text-amber-800`;
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ================================================
  // ANIMACIONES UNIFICADAS
  // ================================================

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      y: 20, 
      opacity: 0,
      scale: 0.95
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const
      }
    }
  };

  // Stats para mostrar
  const stats = {
    completados: proyectos.filter(p => p.status === 'Completado').length,
    enDesarrollo: proyectos.filter(p => p.status === 'En Desarrollo').length,
    planificados: proyectos.filter(p => p.status === 'Planificado').length,
    total: proyectos.length
  };

  // ================================================
  // COMPONENTE PRINCIPAL RENDERIZADO
  // ================================================

  return (
    <>
  <SEO
    title="Proyectos de Software Realizados | Algorithmia Córdoba"
    description="Conocé los proyectos de desarrollo de software de Algorithmia: sistemas de inspecciones PWA, gestión de riesgos laborales, órdenes de trabajo Kanban y más soluciones para empresas en Argentina."
    keywords="proyectos software córdoba, portafolio desarrollo, sistemas gestión, pwa industrial, casos de éxito"
    url="https://algorithmia.com.ar/proyectos"
    canonicalUrl="https://algorithmia.com.ar/proyectos"  // ✅ nuevo
    section="Proyectos"
  />

    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* HEADER REUTILIZABLE */}
      <Header isSplashLoading={false} />

      {/* Hero Section con partículas y gradiente unificado - Ajustado padding-top */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-[#4ADE80]/60"
              style={{
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, (Math.random() - 0.5) * 40],
                x: [0, (Math.random() - 0.5) * 30],
                opacity: [0, 0.6 * Math.random(), 0],
                scale: [1, 1.3, 1]
              }}
              transition={{
                duration: Math.random() * 5 + 4,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: "easeInOut",
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <SparklesIcon className="h-4 w-4 text-[#4ADE80] animate-pulse" />
              <span className="text-gray-200 text-sm font-medium">Nuestro Portafolio</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Proyectos que <span className="bg-gradient-to-r from-[#4ADE80] to-[#3BC571] bg-clip-text text-transparent">Transforman</span> Empresas
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Soluciones digitales innovadoras desarrolladas para optimizar procesos, aumentar la productividad y generar resultados tangibles.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Cards con paleta unificada */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { 
                icon: CheckCircleIcon, 
                value: stats.completados, 
                label: 'Proyectos Completados',
                gradient: `from-[${colorPalette.secondary.green}] to-[${colorPalette.secondary.teal}]`
              },
              { 
                icon: ClockIcon, 
                value: stats.enDesarrollo, 
                label: 'En Desarrollo',
                gradient: `from-[${colorPalette.primary.medium}] to-[${colorPalette.secondary.green}]`
              },
              { 
                icon: LightBulbIcon, 
                value: stats.planificados, 
                label: 'Planificados',
                gradient: 'from-[#F59E0B] to-[#D97706]'
              },
              { 
                icon: ArrowTrendingUpIcon, 
                value: stats.total, 
                label: 'Total de Proyectos',
                gradient: `from-[${colorPalette.primary.dark}] to-[${colorPalette.secondary.green}]`
              }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 flex items-center gap-4 group hover:scale-105"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
                    <span className="text-sm text-gray-600 font-medium">{stat.label}</span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Filtros y Búsqueda mejorados */}
      <section className="py-8 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col lg:flex-row gap-6 items-center justify-between"
          >
            {/* Barra de búsqueda */}
            <div className="relative flex-1 max-w-2xl">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar proyectos por nombre, categoría o tecnología..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-12 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#4ADE80] focus:ring-2 focus:ring-[#4ADE80]/20 transition-all duration-300"
              />
              {searchTerm && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-4 w-4 text-gray-600" />
                </motion.button>
              )}
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'todos', label: 'Todos', icon: CursorArrowRaysIcon, color: 'gray' },
                  { id: 'completados', label: 'Completados', icon: CheckCircleIcon, color: 'green' },
                  { id: 'desarrollo', label: 'En Desarrollo', icon: ClockIcon, color: 'blue' },
                  { id: 'planificados', label: 'Planificados', icon: LightBulbIcon, color: 'amber' }
                ].map((filterOption) => {
                  const Icon = filterOption.icon;
                  return (
                    <motion.button
                      key={filterOption.id}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setFilter(filterOption.id);
                        setCurrentPage(1);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                        filter === filterOption.id 
                          ? `bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white shadow-lg` 
                          : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{filterOption.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Grid de Proyectos mejorado */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProjects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No se encontraron proyectos</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Intenta con otros términos de búsqueda o cambia los filtros
              </p>
            </motion.div>
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {currentProjects.map((proyecto) => {
                  const Icon = proyecto.icon;
                  return (
                    <motion.article
                      key={proyecto.id}
                      variants={itemVariants}
                      whileHover={{ 
                        y: -8,
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                        scale: 1.02
                      }}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 cursor-pointer overflow-hidden group"
                      onClick={() => openProjectDetails(proyecto.id)}
                    >
                      {/* Badge de estado */}
                      <div className="absolute top-4 right-4 z-10">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(proyecto.status)}`}>
                          {proyecto.status}
                        </div>
                      </div>

                      {/* Header */}
                      <div className="p-6">
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`p-3 rounded-xl bg-gradient-to-br ${getGradientClass(proyecto.color)} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#4ADE80] transition-colors duration-300">
                              {proyecto.title}
                            </h3>
                            <span className="text-sm text-gray-500 font-medium">
                              {proyecto.category}
                            </span>
                          </div>
                        </div>

                        {/* Descripción */}
                        <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                          {proyecto.description}
                        </p>

                        {/* Detalles rápidos */}
                        <div className="flex items-center gap-6 mb-6">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <ClockIcon className="h-4 w-4" />
                            <span>{proyecto.timeline}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <CodeBracketIcon className="h-4 w-4" />
                            <span>{proyecto.technologies.length} tecnologías</span>
                          </div>
                        </div>

                        {/* Tecnologías */}
                        <div className="flex flex-wrap gap-2 mb-6">
                          {proyecto.technologies.slice(0, 4).map((tech, idx) => (
                            <span 
                              key={idx} 
                              className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors"
                            >
                              {tech}
                            </span>
                          ))}
                          {proyecto.technologies.length > 4 && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium hover:bg-gray-200 transition-colors">
                              +{proyecto.technologies.length - 4}
                            </span>
                          )}
                        </div>

                        {/* Footer */}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full py-3 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group-hover:from-[#3BC571] group-hover:to-[#2AAE60] relative overflow-hidden"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProjectDetails(proyecto.id);
                          }}
                        >
                          <EyeIcon className="h-5 w-5" />
                          <span className="relative z-10">Ver Detalles</span>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        </motion.button>
                      </div>
                    </motion.article>
                  );
                })}
              </motion.div>

              {/* Paginación */}
              {totalPages > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mt-12 flex items-center justify-between"
                >
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                    <span>Anterior</span>
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return pageNum > 0 && pageNum <= totalPages ? (
                        <button
                          key={pageNum}
                          onClick={() => {
                            setCurrentPage(pageNum);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`w-10 h-10 rounded-lg font-medium transition-all duration-300 hover:scale-110 ${
                            currentPage === pageNum 
                              ? 'bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white shadow-lg' 
                              : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ) : null;
                    }).filter(Boolean)}
                  </div>
                  
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    <span>Siguiente</span>
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </motion.div>
              )}

              {/* Contador */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="mt-8 pt-8 border-t border-gray-200 text-center"
              >
                <span className="text-sm text-gray-600">
                  Mostrando <span className="font-bold text-[#4ADE80]">{indexOfFirstProject + 1}-{Math.min(indexOfLastProject, filteredProjects.length)}</span> de <span className="font-bold text-[#4ADE80]">{filteredProjects.length}</span> proyectos
                </span>
              </motion.div>
            </>
          )}
        </div>
      </section>

      {/* Modal de Detalles Premium */}
      <AnimatePresence>
        {selectedProject && selectedProyecto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeProjectDetails}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del Modal */}
              <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${getGradientClass(selectedProyecto.color)} shadow-lg`}>
                      <selectedProyecto.icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        {selectedProyecto.title}
                      </h2>
                      <div className="flex items-center gap-4">
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                          {selectedProyecto.category}
                        </span>
                        <span className={`px-3 py-1 rounded-lg text-sm font-medium ${getStatusColor(selectedProyecto.status)}`}>
                          {selectedProyecto.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={closeProjectDetails}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-600" />
                  </motion.button>
                </div>
              </div>

              {/* Contenido del Modal */}
              <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Descripción */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <span className="text-[#4ADE80]">📋</span>
                    Descripción del Proyecto
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {selectedProyecto.longDescription}
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50 rounded-xl">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Cliente</span>
                      <span className="text-gray-900 font-semibold">{selectedProyecto.client}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Duración</span>
                      <span className="text-gray-900 font-semibold">{selectedProyecto.timeline}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Tecnologías</span>
                      <span className="text-gray-900 font-semibold">{selectedProyecto.technologies.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Impacto</span>
                      <span className="text-gray-900 font-semibold text-sm">{selectedProyecto.impact}</span>
                    </div>
                  </div>
                </div>

                {/* Características */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <span className="text-[#4ADE80]">✨</span>
                    Características Principales
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedProyecto.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                        className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer border border-transparent hover:border-[#4ADE80]/20"
                      >
                        <div className="p-2 bg-[#4ADE80]/10 rounded-lg">
                          <CheckCircleIcon className="h-5 w-5 text-[#4ADE80]" />
                        </div>
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Tecnologías */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <span className="text-[#2D4A7D]">💻</span>
                    Stack Tecnológico
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedProyecto.technologies.map((tech, idx) => (
                      <motion.span
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.03 }}
                        whileHover={{ scale: 1.1, y: -2 }}
                        className="px-4 py-2 bg-gradient-to-r from-[#2D4A7D] to-[#4ADE80] text-white font-medium rounded-lg shadow-md cursor-default hover:shadow-lg transition-shadow"
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
                  <div className="text-center max-w-2xl mx-auto">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      ¿Te gustaría implementar una solución similar?
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Podemos adaptar esta solución a las necesidades específicas de tu empresa
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <motion.button
                        whileHover={{ 
                          scale: 1.05, 
                          boxShadow: `0 8px 25px ${colorPalette.secondary.green}40` 
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          closeProjectDetails();
                          navigate('/solicitar-propuesta', { 
                            state: { 
                              projectId: selectedProject,
                              projectTitle: selectedProyecto?.title,
                              projectCategory: selectedProyecto?.category 
                            } 
                          });
                        }}
                        className="px-8 py-3 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden"
                      >
                        <RocketLaunchIcon className="h-5 w-5" />
                        <span className="relative z-10">Solicitar Propuesta</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          closeProjectDetails();
                          const event = new CustomEvent('openMeetingModal');
                          window.dispatchEvent(event);
                        }}
                        className="px-8 py-3 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-200 hover:border-[#4ADE80] transition-all duration-300 flex items-center justify-center gap-3 hover:text-[#4ADE80]"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        Agendar Reunión
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer mejorado con paleta unificada */}
      <footer className="py-12 bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <img 
                  src={LogoAlgorithmia} 
                  alt="Algorithmia Logo" 
                  className="h-10 w-auto"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-[#4ADE80] to-[#3BC571] bg-clip-text text-transparent">
                  Algorithmia
                </span>
              </div>
              <p className="text-gray-300 max-w-md">
                Transformando ideas en soluciones digitales
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <motion.button 
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-white/10 hover:bg-white/20 text-gray-200 rounded-lg font-medium transition-colors duration-300 backdrop-blur-sm"
              >
                Volver al Inicio
              </motion.button>
              <motion.button 
                whileHover={{ 
                  scale: 1.05, 
                  y: -2,
                  boxShadow: `0 5px 15px ${colorPalette.secondary.green}40`
                }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const event = new CustomEvent('openContactModal');
                  window.dispatchEvent(event);
                }}
                className="px-6 py-2 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] hover:from-[#3BC571] hover:to-[#2AAE60] text-white rounded-lg font-medium transition-all duration-300 shadow-lg"
              >
                Contacto
              </motion.button>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default ProyectosPage;